import React, { useState, useEffect, createContext, useContext } from 'react'
import { node } from 'prop-types'
import { replaceRoute } from 'lib/routing'
import fitty from 'fitty'

import { INITIAL_ENGRAVING } from 'utils/constants'
import WORDS from 'lib/words'

import NativeAuthContext from 'components/NativeAuth'

const EngravingContext = createContext({})

const EngravingContextProvider = ({ children }) => {
  const [visible, setVisible] = useState(false)
  const [engraving, setEngraving] = useState(INITIAL_ENGRAVING)
  const [currentView, setCurrentView] = useState({ item: 'front', step: 'initialstep' })
  const [engravingProgress, setEngravingProgress] = useState(25)
  const [textError, setTextError] = useState('')
  const [editMode, setEditMode] = useState(false)
  const { user } = useContext(NativeAuthContext)

  useEffect(() => {
    const { item } = currentView
    let progress = 25

    if (item === 'back') progress = 50
    if (item === 'box') progress = 75
    if (item === 'summary') progress = 100

    setEngravingProgress(progress)
  }, [currentView.item, currentView])

  useEffect(() => {
    const { pattern } = engraving.front

    if (pattern?.value.length) {
      setEngraving({
        ...engraving,
        front: {
          ...engraving.front,
          pattern: pattern,
          text: INITIAL_ENGRAVING.front.text,
          miniIcon: INITIAL_ENGRAVING.front.miniIcon
        }
      })
    }
  }, [engraving.front.pattern])

  useEffect(() => {
    const { miniIcon } = engraving.front

    if (miniIcon?.value.length) {
      setEngraving({
        ...engraving,
        front: {
          ...engraving.front,
          miniIcon: miniIcon,
          text: INITIAL_ENGRAVING.front.text,
          pattern: INITIAL_ENGRAVING.front.pattern
        }
      })
    }
  }, [engraving.front.miniIcon])

  useEffect(() => {
    setTextError('')
    let { text } = engraving.front

    if (text.orientation === 'horizontal' && text.value.length > 3) {
      text = {
        ...text,
        value: text.value.substr(0, 3)
      }
    }

    if (text?.value.length) {
      setEngraving({
        ...engraving,
        front: {
          ...engraving.front,
          text: text,
          miniIcon: INITIAL_ENGRAVING.front.miniIcon,
          pattern: INITIAL_ENGRAVING.front.pattern
        }
      })
    }
  }, [engraving.front.text])

  useEffect(() => {
    setTextError('')
    let { text } = engraving.back

    if (text.orientation === 'horizontal' && text.value.length > 3) {
      text = {
        ...text,
        value: text.value.substr(0, 3)
      }
    }

    if (text?.value.length) {
      setEngraving({
        ...engraving,
        back: { ...engraving.back, text }
      })
    }
  }, [engraving.back.text])

  useEffect(() => {
    setTextError('')
    let { text } = engraving.box

    if (text?.value.length) {
      setEngraving({
        ...engraving,
        box: { ...engraving.box, text }
      })
    }
  }, [engraving.box.text])

  useEffect(() => {
    if (engraving) {
      let values = []

      Object.keys(engraving).forEach(key => {
        Object.keys(engraving[key]).forEach(item => {
          if (typeof engraving[key][item] === 'object') {
            values = [...values, engraving[key][item].value]
          }
        })
      })

      if (values.filter(value => value !== '').length === 0 && engraving.proceeded) {
        resetEngraving()
      }
    }

    if (engraving && currentView) {
      const fits = document.getElementsByClassName('fit')

      for (let fit of fits) {
        fitty(fit, {
          minSize: 6,
          maxSize: 32,
          multiLine: false
        })
      }
    }
  }, [engraving, currentView])

  useEffect(() => {
    if (user) {
      setEngraving({
        ...engraving,
        box: {
          ...engraving.box,
          text: {
            ...engraving.box.text,
            value: user.firstName
          }
        }
      })
    }
  }, [user])

  const proceedItem = (item, step = null, proceeded = true) => {
    const forbiddenWord = step === 'text' && WORDS.includes(engraving[item][step].value.toLowerCase())

    if (forbiddenWord) {
      setTextError('This text is not allowed')
    } else {
      setEngraving({
        ...engraving,
        [item]: {
          ...engraving[item],
          proceeded: proceeded
        }
      })
    }
  }

  const skipItem = item => {
    setEngraving({
      ...engraving,
      [item]: {
        ...INITIAL_ENGRAVING[item],
        proceeded: true
      }
    })
  }

  const setProgress = item => {
    if (engraving[item].proceeded) {
      let currentEngraving = { ...engraving }
      const items = Object.keys(engraving).filter(key => typeof engraving[key] === 'object')
      const itemIndex = items.indexOf(item)

      items.forEach((key, index) => {
        if (index >= itemIndex) {
          currentEngraving = {
            ...currentEngraving,
            [key]: {
              ...INITIAL_ENGRAVING[key],
              proceeded: false
            }
          }
        }
      })

      setEngraving(currentEngraving)
    }
  }

  const close = () => {
    setEngraving(INITIAL_ENGRAVING)
    setVisible(false)
  }

  const onEdit = (engraving, index, variantId) => {
    setEngraving({
      ...engraving,
      proceeded: false,
      lineItemIndex: index,
      variantId
    })

    setEditMode(true)
    replaceRoute('/buy-online/epod')
    // replaceRoute('/buy-online/epod?engraving=editMode')
    setVisible(true)
  }

  const resetEngraving = () => {
    setEngraving(INITIAL_ENGRAVING)
    setVisible(false)
    setEditMode(false)
    setCurrentView({ item: 'front', step: 'initialstep' })
    setEngravingProgress(25)
    setTextError('')
  }

  const value = {
    visible,
    setVisible,
    engraving,
    setEngraving,
    editMode,
    setEditMode,
    currentView,
    setCurrentView,
    textError,
    engravingProgress,
    proceedItem,
    skipItem,
    setProgress,
    close,
    onEdit,
    resetEngraving
  }

  return <EngravingContext.Provider value={value}>{children}</EngravingContext.Provider>
}

EngravingContextProvider.propTypes = {
  children: node.isRequired
}

export { EngravingContext, EngravingContextProvider }
